/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Widget from 'components/Widget';
import PlaceholderSpinner from 'components/kea/placeholders/PlaceholderSpinner';
import PlaceholderError from 'components/kea/placeholders/PlaceholderError';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import {
  loadAnalyticsReport,
  selectTopQueries,
  selectZeroResults,
  selectLostOpportunities,
  selectTimeframe,
  actions as analyticsActions,
} from 'slices/AnalyticsSlice';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { Links } from 'containers/AppLayout/menu/linkBuilder';
import { formatCompactMetric } from 'util/chartUtils';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import { buildQueryDetailTarget } from '../components/QueryDetailLink';
import HealthKpiRow from './HealthKpiRow';
import AnalyticsTrendChart from './AnalyticsTrendChart';
import AnalyticsDateRangePicker from '../components/AnalyticsDateRangePicker';
import styles from './HealthDashboard.module.scss';

/**
 * Bridge-backed Search Health Dashboard.
 *
 * Data sourced exclusively from existing analytics bridge calls:
 *   - analytics.top_queries   → total searches, click events, CTR trend
 *   - analytics.zero_results  → zero-result count + daily pv curve
 *   - analytics.lost_opportunities → low-interaction query preview
 *
 * All KPIs are event-level only:
 *   - "Searches"     = search event count (not unique users, not sessions)
 *   - "Click Events" = click event count (one search may produce multiple clicks)
 *   - Click Rate     = click_events / search_events  (event-level ratio, may exceed 1.0 per query)
 *
 * No derived "no action" arithmetic — avoids the case where clicks > searches
 * on a per-day basis makes the subtraction produce a negative value.
 *
 * No new bridge actions required — all data is derived from the three
 * reports already loaded by the individual analytics pages.
 */

// ---------------------------------------------------------------------------
// PreviewCard — mini-table for the bottom row, with optional "show more" CTA
// ---------------------------------------------------------------------------
const PreviewCard = ({
  title, tone, data, isFetching, error, onViewMore, queryTarget, labelQuery, labelSearches, labelShowMore, labelNoData,
}) => {
  const rows = Array.isArray(data) ? data.slice(0, 5) : [];
  const maximumSearches = rows.reduce((maximum, row) => Math.max(maximum, row.searches ?? 0), 0);

  return (
    <Widget styleName={`jr-card-full noMargin ${styles.previewCard} ${styles[tone] || ''}`}>
      <div className={styles.previewHeader}>
        <h3 className={styles.previewTitle}>{title}</h3>
      </div>
      {isFetching && <PlaceholderSpinner height={160} />}
      {!isFetching && error && <PlaceholderError error={error} height={160} />}
      {!isFetching && !error && (
        <>
          <table className={styles.previewTable}>
            <thead>
              <tr>
                <th>{labelQuery}</th>
                <th>{labelSearches}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.query || i}>
                  <td className={styles.queryCell} title={row.query}>
                    <span className={styles.rank}>{String(i + 1).padStart(2, '0')}</span>
                    {queryTarget ? (
                      <Link className={styles.queryLink} to={queryTarget(row.query)}>
                        {row.query}
                      </Link>
                    ) : row.query}
                  </td>
                  <td className={styles.numberCell}>
                    <span
                      className={styles.miniBar}
                      style={{ '--bar-width': `${maximumSearches ? ((row.searches ?? 0) / maximumSearches) * 100 : 0}%` }}
                    >
                      <span className={styles.numberValue}>{row.searches ?? 0}</span>
                    </span>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={2} className={styles.emptyRow}>
                    {labelNoData}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {onViewMore && (
            <div className={styles.previewAction}>
              <button
                type="button"
                onClick={onViewMore}
                className={styles.viewMore}
              >
                {labelShowMore}
                <span className={styles.arrow} aria-hidden="true">→</span>
              </button>
            </div>
          )}
        </>
      )}
    </Widget>
  );
};

PreviewCard.propTypes = {
  title:          PropTypes.string.isRequired,
  tone:           PropTypes.oneOf(['searches', 'zeroResults', 'lostOpportunities']),
  data:           PropTypes.array,
  isFetching:     PropTypes.bool,
  error:          PropTypes.any,
  onViewMore:     PropTypes.func,
  queryTarget:    PropTypes.func,
  labelQuery:     PropTypes.string.isRequired,
  labelSearches:  PropTypes.string.isRequired,
  labelShowMore:  PropTypes.string.isRequired,
  labelNoData:    PropTypes.string.isRequired,
};

PreviewCard.defaultProps = {
  tone:       'searches',
  data:       null,
  isFetching: false,
  error:      null,
  onViewMore: null,
  queryTarget: null,
};

// ---------------------------------------------------------------------------
// Main dashboard component
// ---------------------------------------------------------------------------
export function buildTrendData(searchCurve = [], zeroCurve = []) {
  const rows = new Map();
  searchCurve.forEach((row) => {
    rows.set(row.label, {
      label: row.label,
      searches: row.total ?? 0,
      clickRate: row.ctr ?? 0,
      zeroResults: 0,
      clickEvents: row.click ?? 0,
    });
  });
  zeroCurve.forEach((row) => {
    const existing = rows.get(row.label) || {
      label: row.label,
      searches: 0,
      clickRate: 0,
      zeroResults: 0,
      clickEvents: 0,
    };
    rows.set(row.label, { ...existing, zeroResults: row.pv ?? 0 });
  });
  return Array.from(rows.values());
}

const HealthDashboardBridge = ({ history }) => {
  const dispatch        = useDispatch();
  const localizeMessage = useLocalizedMessage();
  const topQueries      = useSelector(selectTopQueries);
  const zeroResults     = useSelector(selectZeroResults);
  const lostOpp         = useSelector(selectLostOpportunities);
  const timeframe       = useSelector(selectTimeframe);
  const { currentShop } = useSelector((store) => store.app);
  const { capabilities = {} } = useShellAppContext();
  const location = useLocation();
  const [selectedMetric, setSelectedMetric] = useState('searches');

  useEffect(() => {
    if (!currentShop) return;
    dispatch(loadAnalyticsReport({ shopId: currentShop, reportType: 'top_queries' }));
    dispatch(loadAnalyticsReport({ shopId: currentShop, reportType: 'zero_results' }));
    dispatch(loadAnalyticsReport({ shopId: currentShop, reportType: 'lost_opportunities' }));
  }, [dispatch, currentShop, timeframe]);

  const handleTimeframeChange = (range) => {
    dispatch(analyticsActions.setTimeframe(range));
  };

  const lblQuery    = localizeMessage('analytics.table.query');
  const lblSrch     = localizeMessage('analytics.table.searches');
  const lblShowMore = localizeMessage('common.showMore');
  const lblNoData   = localizeMessage('search.health.preview.noData');

  // ── KPI numbers ───────────────────────────────────────────────────────────
  const curveItems    = (topQueries.curve && topQueries.curve.data) ? topQueries.curve.data : [];
  const zeroCurveItems = (zeroResults.curve && zeroResults.curve.data) ? zeroResults.curve.data : [];
  const trendData = useMemo(
    () => buildTrendData(curveItems, zeroCurveItems),
    [curveItems, zeroCurveItems],
  );
  const isLoadingMain = topQueries.isFetching;
  const isLoadingZero = zeroResults.isFetching;
  const isLoadingLost = lostOpp.isFetching;

  const trendMetrics = {
    searches: {
      dataKey: 'searches',
      seriesLabel: localizeMessage('search.health.trend.label.searches'),
      title: localizeMessage('search.health.trend.searches.title'),
      subtitle: localizeMessage('search.health.trend.searches.subtitle'),
      tone: 'searches',
      valueFormatter: (value) => Number(value).toLocaleString(),
      axisFormatter: formatCompactMetric,
      error: topQueries.error,
      isFetching: isLoadingMain,
    },
    clickRate: {
      dataKey: 'clickRate',
      seriesLabel: localizeMessage('search.health.kpi.clickRate'),
      title: localizeMessage('search.health.trend.clickRate.title'),
      subtitle: localizeMessage('search.health.trend.clickRate.subtitle'),
      tone: 'clickRate',
      valueFormatter: (value) => `${Number(value).toFixed(1)}%`,
      axisFormatter: (value) => `${Number(value).toFixed(0)}%`,
      error: topQueries.error,
      isFetching: isLoadingMain,
    },
    zeroResults: {
      dataKey: 'zeroResults',
      seriesLabel: localizeMessage('search.health.kpi.zeroResult'),
      title: localizeMessage('search.health.trend.zeroResults.title'),
      subtitle: localizeMessage('search.health.trend.zeroResults.subtitle'),
      tone: 'zeroResults',
      valueFormatter: (value) => Number(value).toLocaleString(),
      axisFormatter: formatCompactMetric,
      error: zeroResults.error,
      isFetching: isLoadingZero,
    },
    clickEvents: {
      dataKey: 'clickEvents',
      seriesLabel: localizeMessage('search.health.trend.label.clicks'),
      title: localizeMessage('search.health.trend.clickEvents.title'),
      subtitle: localizeMessage('search.health.trend.clickEvents.subtitle'),
      tone: 'clickEvents',
      valueFormatter: (value) => Number(value).toLocaleString(),
      axisFormatter: formatCompactMetric,
      error: topQueries.error,
      isFetching: isLoadingMain,
    },
  };
  const activeTrend = trendMetrics[selectedMetric];

  const reportRoutes = {
    top_queries: Links.searchQueryPopular(currentShop),
    zero_results: Links.searchQueryNoResult(currentShop),
    lost_opportunities: Links.searchLostOpportunities(currentShop),
  };
  const navigateToReport = (reportType) => history.push(reportRoutes[reportType]);
  const queryTarget = capabilities.analyticsQueryDetailV1
    ? query => buildQueryDetailTarget(
      currentShop,
      query,
      `${location.pathname}${location.search}`,
    )
    : null;

  return (
    <SearchFeaturePageShell
      pageId="analyticHealth"
      actions={(
        <AnalyticsDateRangePicker
          timeframe={timeframe}
          onSubmitRange={handleTimeframeChange}
          isFetching={isLoadingMain}
          localizeMessage={localizeMessage}
        />
      )}
    >
      <Grid container spacing={3}>

        {/* ── Row 1: KPI Cards ─────────────────────────────────────────────── */}

        <HealthKpiRow
          topQueries={topQueries}
          zeroResults={zeroResults}
          localizeMessage={localizeMessage}
          selectedMetric={selectedMetric}
          onSelectMetric={setSelectedMetric}
        />

        {/* ── Row 2: Main Trend Chart ───────────────────────────────────────── */}

        <Grid item xs={12}>
          <AnalyticsTrendChart
            data={trendData}
            dataKey={activeTrend.dataKey}
            seriesLabel={activeTrend.seriesLabel}
            title={activeTrend.title}
            subtitle={activeTrend.subtitle}
            tone={activeTrend.tone}
            valueFormatter={activeTrend.valueFormatter}
            axisFormatter={activeTrend.axisFormatter}
            noDataLabel={lblNoData}
            isFetching={activeTrend.isFetching}
            error={activeTrend.error}
          />
        </Grid>

        {/* ── Row 3: Preview Tables ─────────────────────────────────────────── */}

        <Grid item xs={12} md={4}>
          <PreviewCard
            title={localizeMessage('search.health.preview.topQueries')}
            tone="searches"
            data={topQueries.data}
            isFetching={isLoadingMain}
            error={topQueries.error}
            onViewMore={() => navigateToReport('top_queries')}
            queryTarget={queryTarget}
            labelQuery={lblQuery}
            labelSearches={lblSrch}
            labelShowMore={lblShowMore}
            labelNoData={lblNoData}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <PreviewCard
            title={localizeMessage('search.health.preview.zeroResults')}
            tone="zeroResults"
            data={zeroResults.data}
            isFetching={isLoadingZero}
            error={zeroResults.error}
            onViewMore={() => navigateToReport('zero_results')}
            queryTarget={queryTarget}
            labelQuery={lblQuery}
            labelSearches={lblSrch}
            labelShowMore={lblShowMore}
            labelNoData={lblNoData}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <PreviewCard
            title={localizeMessage('search.health.preview.lostOpportunities')}
            tone="lostOpportunities"
            data={lostOpp.data}
            isFetching={isLoadingLost}
            error={lostOpp.error}
            onViewMore={() => navigateToReport('lost_opportunities')}
            queryTarget={queryTarget}
            labelQuery={lblQuery}
            labelSearches={lblSrch}
            labelShowMore={lblShowMore}
            labelNoData={lblNoData}
          />
        </Grid>

      </Grid>
    </SearchFeaturePageShell>
  );
};

HealthDashboardBridge.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
};

export default HealthDashboardBridge;
